import { ActionButtonProps } from '@euler/app/components/BottomAction';
import { MemberPickerBottomSheet } from '@euler/app/components/MemberPickerBottomSheet';
import { PageContainerForIpad } from '@euler/app/components/PageContainerForIpad';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { Colors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { useContainerLayout } from '@euler/components/layout/LayoutProvider';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsLandscape } from '@euler/functions/responsiveness';
import { OrgUserRoleType } from '@euler/model/enum';
import { memo, useCallback, useMemo, useRef } from 'react';
import {
  Platform,
  ScrollView,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { Fill } from 'react-slot-fill';
import { useFacadeInspection } from '../functions/useFacadeInspection';
import { usePreInspectionActions } from '../functions/usePreInspectionActions';
import { WiredDashboardInspection } from './DashboardInspection';
import { FacadeMediaList } from './FacadeInspectionMediaListView';
import {
  FacadeSitesView,
  useFacadeSitesViewHeightByContentWidth,
} from './FacadeSitesView';
import { WiredPreInspectionSignature } from './PreInspectionSignature';

const kMatchingRoles = [OrgUserRoleType.ServiceAgents, OrgUserRoleType.Manager];

const kTroubleLightContainerHeight = 60;
const kSignatureViewHeight = 200;
const kDefaultAspectRatio = 9 / 6;
const kSignatureViewMarginTop = 15;
const kMediaCellMargin = 10;
const kContentPadding = 35;
const kContentRightMargin = 20;
const kFacadeSitesViewPadding = 15;

const useContentLayouts = (args: {
  width: number;
  height: number;
  leftWidthFlex: number;
  isLandscape: boolean;
}) => {
  const { rpx } = useResponsiveContext();
  const { height, leftWidthFlex, isLandscape } = args;
  const contentWidth = args.width - rpx(kContentPadding) * 2;
  const contentWidthLeft =
    (contentWidth - rpx(kContentRightMargin)) * leftWidthFlex;
  const contentWidthRight =
    contentWidth - contentWidthLeft - rpx(kContentRightMargin);
  const facadeSitesViewContentWidth =
    contentWidthRight - 2 * rpx(kFacadeSitesViewPadding);
  const facadeSitesViewHeight = useFacadeSitesViewHeightByContentWidth(
    facadeSitesViewContentWidth,
    {
      illustrationViewMargin: rpx(kFacadeSitesViewPadding),
    },
  );
  const mediaCellSize =
    height - facadeSitesViewHeight - rpx(kMediaCellMargin) * 2;
  const leftBottomHeight = rpx(
    kTroubleLightContainerHeight +
      kSignatureViewHeight +
      kSignatureViewMarginTop +
      kMediaCellMargin,
  );
  const dashboardImageWidth = contentWidthLeft;
  let dashboardImageHeight = dashboardImageWidth / kDefaultAspectRatio;

  if (isLandscape) {
    const totalLeftHeight = dashboardImageHeight + leftBottomHeight;
    if (totalLeftHeight > height) {
      dashboardImageHeight = height - rpx(kTroubleLightContainerHeight);
    } else {
      dashboardImageHeight = height - leftBottomHeight;
    }
  }

  return {
    contentWidthLeft,
    contentWidthRight,
    mediaCellSize,
    dashboardImageWidth,
    dashboardImageHeight,
    facadeSitesViewContentWidth,
  };
};

export const WiredPreInspectionScreenContentForIpad = memo(() => {
  const moreButtonRef = useRef(null);
  const { rpx } = useResponsiveContext();
  const {
    onViewReport,
    showActions,
    user,
    detail,
    userPickerInfo: {
      onUserPickerPress,
      isUserPickerVisible,
      onUserSelected,
      onUserPickerDismiss,
    },
  } = usePreInspectionActions({ strict: true });

  const actions = useMemo(() => {
    const res: ActionButtonProps[] = [
      {
        bordered: true,
        text: '预览报告',
        variant: 'white',
        onPress: onViewReport,
      },
      {
        bordered: true,
        text: '更多',
        variant: 'white',
        onPress: () => showActions(moreButtonRef),
        style: { marginLeft: rpx(15) },
        buttonRef: moreButtonRef,
        iconRight: (
          <Icon name="more-vertical" size={rpx(24)} color={Colors.White} />
        ),
      },
    ];
    return res;
  }, [onViewReport, rpx, showActions]);

  return (
    <>
      <PageContainerForIpad
        name="preInspection"
        title="车辆预检"
        actions={actions}
        headerStyle={{ paddingTop: rpx(15), paddingBottom: rpx(15) }}
        footerStyle={{ paddingTop: rpx(15), paddingBottom: rpx(15) }}
        contentContainerStyle={{
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
        }}
      >
        <Content />

        <Fill name="preInspection/page-container/footer/left">
          <TouchableOpacity
            onPress={onUserPickerPress}
            css={`
              flex-direction: row;
              align-items: center;
            `}
          >
            <Typography.Label
              light
              color={Colors.White7_5}
              size={rpx(18)}
              lineHeight={rpx(24)}
            >
              服务顾问: {user.name}
            </Typography.Label>
            <Icon
              name="pencil"
              color={Colors.White7_5}
              size={rpx(12)}
              css={`
                margin-left: ${rpx(8)}px;
              `}
            />
          </TouchableOpacity>
        </Fill>

        <Fill name="preInspection/page-container/header/right">
          <View
            css={`
              align-items: flex-end;
            `}
          >
            <Typography.Label
              bold
              size={rpx(28)}
              color={'white'}
              css={`
                margin-left: ${rpx(10)}px;
              `}
            >
              {detail.basicInfo.licensePlateNo}
            </Typography.Label>
            <Typography.Label
              regular
              size={rpx(16)}
              color={Colors.White4}
              numberOfLines={1}
              css={`
                margin-left: ${rpx(10)}px;
              `}
            >
              {detail.basicInfo.vehicleName}
            </Typography.Label>
          </View>
        </Fill>
      </PageContainerForIpad>
      <MemberPickerBottomSheet
        show={isUserPickerVisible}
        title={'修改接车预检服务顾问'}
        role={kMatchingRoles}
        onSelect={onUserSelected}
        onDismiss={onUserPickerDismiss}
      />
    </>
  );
});

const Content = memo(() => {
  const { width, height } = useContainerLayout();
  const { rpx } = useResponsiveContext();
  const isLandscape = useIsLandscape();
  const leftWidthFlex = isLandscape ? 0.55 : 0.55;

  const {
    medias,
    facadeSites,
    siteInspections,
    requiredSites,
    frontRecordCount,
    rearRecordCount,
    onPreviewVideo,
    onSitePress,
    onDeleteMedia,
    onRetryMediaUpload,
  } = useFacadeInspection();

  const {
    contentWidthLeft,
    contentWidthRight,
    mediaCellSize,
    dashboardImageWidth,
    dashboardImageHeight,
    facadeSitesViewContentWidth,
  } = useContentLayouts({
    width,
    height,
    isLandscape,
    leftWidthFlex,
  });

  const EmptyMediasView = useCallback(() => {
    return (
      <EmptyMediaListView
        iconSize={rpx(isLandscape ? 44 : 88)}
        css={`
          width: ${isLandscape
            ? contentWidthRight
            : contentWidthRight - rpx(40)}px;
          height: ${isLandscape ? mediaCellSize : height - rpx(20)}px;
        `}
      />
    );
  }, [contentWidthRight, height, isLandscape, mediaCellSize, rpx]);

  return (
    <HStack
      alignItems="stretch"
      css={`
        flex: 1;
        padding: 0 ${rpx(kContentPadding)}px;
      `}
    >
      <ScrollView
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
        css={`
          flex: ${leftWidthFlex * 10};
        `}
      >
        <WiredDashboardInspection
          noShadow
          width={dashboardImageWidth}
          height={dashboardImageHeight}
          shadeStyle={{ backgroundColor: 'transparent' }}
          troubleLightsLabelStyle={{ color: Colors.White8 }}
          troubleLightsContainerStyle={{
            height: rpx(kTroubleLightContainerHeight),
            paddingLeft: 0,
            paddingRight: 0,
            marginTop: 0,
          }}
          retakeButtonSize={rpx(isLandscape ? 88 : 44)}
          touchableImageContainerStyle={{
            backgroundColor: '#fff',
          }}
          troubleLightsPlaceholderTextStyle={{
            color: Colors.White4_5,
          }}
          troubleLightsPlaceholderIconColor={Colors.White4_5}
        />

        {!isLandscape && (
          <FacadeSitesView
            facadeSites={facadeSites}
            siteInspections={siteInspections}
            requiredSites={requiredSites}
            frontRecordCount={frontRecordCount}
            rearRecordCount={rearRecordCount}
            contentWidth={contentWidthLeft}
            onSitePress={onSitePress}
            css={`
              border-radius: ${rpx(20)}px;
              padding-left: ${rpx(kFacadeSitesViewPadding)}px;
              padding-right: ${rpx(kFacadeSitesViewPadding)}px;
            `}
          />
        )}

        <WiredPreInspectionSignature
          css={`
            margin-top: ${rpx(kSignatureViewMarginTop)}px;
            border-radius: ${rpx(20)}px;
          `}
        />
      </ScrollView>
      {isLandscape ? (
        <ScrollView
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          css={`
            flex: ${(1 - leftWidthFlex) * 10};
            margin-left: ${rpx(kContentRightMargin)}px;
          `}
        >
          <FacadeSitesView
            facadeSites={facadeSites}
            siteInspections={siteInspections}
            requiredSites={requiredSites}
            frontRecordCount={frontRecordCount}
            rearRecordCount={rearRecordCount}
            contentWidth={facadeSitesViewContentWidth}
            onSitePress={onSitePress}
            css={`
              border-radius: ${rpx(20)}px;
              padding-left: ${rpx(kFacadeSitesViewPadding)}px;
              padding-right: ${rpx(kFacadeSitesViewPadding)}px;
            `}
          />
          <FacadeMediaList
            medias={medias}
            width={width - contentWidthLeft - rpx(40)}
            cellSize={mediaCellSize}
            horizontal
            onDelete={onDeleteMedia}
            onRetryUpload={onRetryMediaUpload}
            onPreviewVideo={onPreviewVideo}
            contentContainerStyle={{
              flexGrow: 1,
              paddingTop: rpx(kMediaCellMargin),
            }}
            ListEmptyComponent={EmptyMediasView}
          />
        </ScrollView>
      ) : (
        <View
          css={`
            flex: ${(1 - leftWidthFlex) * 10};
            border-radius: ${rpx(20)}px;
            margin-top: ${rpx(-20)}px;
          `}
        >
          <FacadeMediaList
            medias={medias}
            width={contentWidthRight - rpx(40)}
            onDelete={onDeleteMedia}
            onRetryUpload={onRetryMediaUpload}
            onPreviewVideo={onPreviewVideo}
            showsVerticalScrollIndicator={Platform.OS === 'web'}
            contentContainerStyle={{
              flexGrow: 1,
              paddingTop: rpx(20),
              paddingLeft: rpx(20),
              paddingRight: rpx(20),
            }}
            ListEmptyComponent={EmptyMediasView}
            css={`
              margin-right: ${rpx(-20)}px;
            `}
          />
        </View>
      )}
    </HStack>
  );
});

const EmptyMediaListView = memo(
  ({
    iconSize,
    style,
  }: {
    style?: StyleProp<ViewStyle>;
    iconSize?: number;
  }) => {
    const { rpx } = useResponsiveContext();
    iconSize = iconSize ?? rpx(88);
    return (
      <Center
        css={`
          flex: 1;
        `}
        style={style}
      >
        <Icon name="file-tray-outline" color={Colors.White5} size={iconSize} />
        <Typography.Label
          color={Colors.White5_5}
          size={rpx(20)}
          css={`
            margin-top: ${rpx(10)}px;
          `}
        >
          点击车辆外观部位开始检测
        </Typography.Label>
      </Center>
    );
  },
);
